import { useEffect, useState } from "react";
import { apiClient, API } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Download, Search, Mail, Phone } from "lucide-react";
import { PageHeader, Card, inputCls, btnGhost, btnDanger } from "@/components/admin/ui";

const STATUSES = [
    { v: "all", l: "All" },
    { v: "pending", l: "Pending" },
    { v: "replied", l: "Replied" },
    { v: "closed", l: "Closed" },
];

export default function InquiriesAdminPage() {
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("all");
    const [loading, setLoading] = useState(true);

    const fetchItems = () => {
        setLoading(true);
        const params = new URLSearchParams();
        if (q) params.set("q", q);
        if (status !== "all") params.set("status", status);
        apiClient.get(`/inquiries${params.toString() ? `?${params}` : ""}`).then((r) => setItems(r.data)).finally(() => setLoading(false));
    };

    useEffect(() => {
        const t = setTimeout(fetchItems, 250);
        return () => clearTimeout(t);
    }, [q, status]); // eslint-disable-line

    const updateStatus = async (id, reply_status) => {
        await apiClient.patch(`/inquiries/${id}`, { reply_status, read: true });
        fetchItems();
    };

    const del = async (id) => {
        if (!window.confirm("Delete this inquiry?")) return;
        await apiClient.delete(`/inquiries/${id}`);
        toast.success("Deleted");
        fetchItems();
    };

    const exportCsv = () => {
        const token = localStorage.getItem("jassi_token");
        // Use fetch to include the Authorization header, then trigger download
        fetch(`${API}/inquiries/export.csv`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.blob())
            .then((blob) => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "inquiries.csv";
                a.click();
                URL.revokeObjectURL(url);
            });
    };

    return (
        <>
            <PageHeader
                title="Customer Inquiries"
                subtitle="Search, filter, reply-status tracking and CSV export."
                action={<button onClick={exportCsv} data-testid="inquiries-export" className={btnGhost}><Download size={14} /> Export CSV</button>}
            />

            <div className="flex flex-col md:flex-row gap-3 mb-6">
                <div className="relative flex-1">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-muted" />
                    <input
                        data-testid="inquiries-search"
                        placeholder="Search name, email, phone, message…"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        className={`${inputCls} pl-10`}
                    />
                </div>
                <select data-testid="inquiries-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className={`${inputCls} md:w-48`}>
                    {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                </select>
            </div>

            {loading ? <div className="text-stone-muted">Loading…</div> : (
                <div className="space-y-3" data-testid="inquiries-list">
                    {items.map((i) => (
                        <Card key={i.id} className={`p-5 ${!i.read ? "border-l-4 border-l-walnut" : ""}`} data-testid={`inquiry-${i.id}`}>
                            <div className="flex flex-col md:flex-row md:justify-between gap-3 mb-3">
                                <div>
                                    <div className="font-medium text-ink dark:text-stone-100">{i.name}</div>
                                    <div className="flex flex-wrap gap-3 text-xs text-stone-muted mt-1">
                                        <a href={`mailto:${i.email}`} className="inline-flex items-center gap-1 hover:text-walnut"><Mail size={12} /> {i.email}</a>
                                        <a href={`tel:${i.phone}`} className="inline-flex items-center gap-1 hover:text-walnut"><Phone size={12} /> {i.phone}</a>
                                        <span>{new Date(i.created_at).toLocaleString()}</span>
                                    </div>
                                </div>
                                <div className="flex gap-2 items-start">
                                    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 ${i.reply_status === "replied" ? "bg-green-100 text-green-800" : i.reply_status === "closed" ? "bg-stone-200 text-stone-700" : "bg-walnut text-cream"}`}>{i.reply_status}</span>
                                </div>
                            </div>
                            {i.service && <div className="text-xs uppercase tracking-widest text-walnut-light mb-2">{i.service}</div>}
                            <p className="text-sm text-stone-700 dark:text-stone-300 whitespace-pre-wrap">{i.message}</p>
                            <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-stone-border dark:border-stone-800">
                                {i.reply_status !== "replied" && <button onClick={() => updateStatus(i.id, "replied")} data-testid={`reply-${i.id}`} className={btnGhost}>Mark replied</button>}
                                {i.reply_status !== "closed" && <button onClick={() => updateStatus(i.id, "closed")} className={btnGhost}>Close</button>}
                                {i.reply_status !== "pending" && <button onClick={() => updateStatus(i.id, "pending")} className={btnGhost}>Reopen</button>}
                                <button onClick={() => del(i.id)} data-testid={`delete-inquiry-${i.id}`} className={btnDanger}><Trash2 size={12} /> Delete</button>
                            </div>
                        </Card>
                    ))}
                    {!items.length && <div className="text-stone-muted py-12 text-center">No inquiries match.</div>}
                </div>
            )}
        </>
    );
}
